/**
 * RPDM Wave 2-UI · Phase 2 · Recursive Hierarchy Node.
 *
 * Renders a single node (workstream / milestone / work-item) and its
 * children recursively. Purely read-only:
 *
 *   * No context menu.
 *   * No drag handle.
 *   * No inline edit / rename / delete controls.
 *   * No create-child affordance.
 *
 * Expand / collapse is a local per-node UI concern (not persisted).
 * Each node carries a stable `data-testid` of the form
 * `hierarchy-node-<id>` and the expand toggle carries
 * `hierarchy-expand-<id>` per Rev.2 § 20.
 *
 * Ordering is preserved from the backend — the component NEVER
 * re-sorts children.
 */
import { useState } from "react";

function labelForKind(kind) {
    switch (kind) {
        case "workstream": return "Workstream";
        case "milestone":  return "Milestone";
        case "work_item":  return "Work item";
        default:            return "Node";
    }
}

function LeftMarker({ kind }) {
    // Purely visual: a small kind badge. Not interactive.
    return (
        <span
            data-testid="rpdm-hierarchy-node-kind"
            className="inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600"
        >
            {labelForKind(kind)}
        </span>
    );
}

function hasChildren(kind, node) {
    if (kind === "workstream") {
        const m = Array.isArray(node?.milestones) ? node.milestones.length : 0;
        const o = Array.isArray(node?.orphan_work_items) ? node.orphan_work_items.length : 0;
        return (m + o) > 0;
    }
    if (kind === "milestone") {
        return Array.isArray(node?.work_items) && node.work_items.length > 0;
    }
    if (kind === "work_item") {
        return Array.isArray(node?.children) && node.children.length > 0;
    }
    return false;
}

function renderChildren(kind, node) {
    if (kind === "workstream") {
        return [
            ...(node.milestones || []).map((m) => (
                <HierarchyTreeNode key={m.id} kind="milestone" node={m} depth={1} />
            )),
            ...(node.orphan_work_items || []).map((wi) => (
                <HierarchyTreeNode key={wi.id} kind="work_item" node={wi} depth={1} />
            )),
        ];
    }
    if (kind === "milestone") {
        return (node.work_items || []).map((wi) => (
            <HierarchyTreeNode key={wi.id} kind="work_item" node={wi} depth={1} />
        ));
    }
    if (kind === "work_item") {
        return (node.children || []).map((c) => (
            <HierarchyTreeNode key={c.id} kind="work_item" node={c} depth={(node.depth ?? 0) + 1} />
        ));
    }
    return null;
}

function TitleFor(kind, node) {
    if (kind === "work_item") {
        const number = node.work_item_number ? `${node.work_item_number} · ` : "";
        return `${number}${node.title || node.id}`;
    }
    return node.name || node.id;
}

function ArchivedBadge({ archived }) {
    if (!archived) return null;
    return (
        <span
            data-testid="rpdm-hierarchy-node-archived"
            className="inline-flex shrink-0 items-center rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600"
        >
            archived
        </span>
    );
}

function StateBadge({ state }) {
    if (!state) return null;
    return (
        <span
            data-testid="rpdm-hierarchy-node-state"
            className="inline-flex shrink-0 items-center rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
        >
            {state}
        </span>
    );
}

export default function HierarchyTreeNode({ kind, node, depth }) {
    const [expanded, setExpanded] = useState(true);
    const parented = hasChildren(kind, node);
    const canToggle = parented;
    const indentPx = (depth || 0) * 16;

    return (
        <li
            role="treeitem"
            aria-expanded={parented ? (expanded ? "true" : "false") : undefined}
            data-testid={`hierarchy-node-${node.id}`}
            className="list-none"
        >
            <div
                className="flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 hover:border-slate-200 hover:bg-slate-50"
                style={{ marginLeft: `${indentPx}px` }}
            >
                {canToggle ? (
                    <button
                        type="button"
                        data-testid={`hierarchy-expand-${node.id}`}
                        onClick={() => setExpanded((prev) => !prev)}
                        aria-label={expanded ? "Collapse" : "Expand"}
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-200"
                    >
                        <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
                    </button>
                ) : (
                    <span className="inline-block h-5 w-5 shrink-0" aria-hidden="true" />
                )}
                <LeftMarker kind={kind} />
                <span
                    data-testid={`hierarchy-node-${node.id}-title`}
                    className="truncate text-sm text-slate-800"
                    title={TitleFor(kind, node)}
                >
                    {TitleFor(kind, node)}
                </span>
                <StateBadge state={kind === "work_item" ? node.state : null} />
                <ArchivedBadge archived={kind !== "work_item" && node.archived} />
            </div>
            {parented && expanded ? (
                <ul className="flex flex-col" role="group">
                    {renderChildren(kind, node)}
                </ul>
            ) : null}
        </li>
    );
}
